import { Record } from 'immutable';
import type { RecordOf } from 'immutable';
import * as React from 'react';
import { useDispatch } from 'react-redux';

import { actionHelpers } from 'app-components/flux/core';

import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

// @ts-expect-error FIXME TS2614
import type { StdDialogProps } from 'components/Dialogs/StdDialog';

import StdDialog from 'components/Dialogs/StdDialog';
import type { AnyAction } from 'redux';

export type DispatchDialogProps = Omit<StdDialogProps, 'children'> & {
  type?: string;
  payload?: RecordOf<any>;
  meta?: any;
  repeat?: number;
  delay?: number;
};

const DispatchDialog = (props: DispatchDialogProps) => {
  const dispatch = useDispatch();
  const {
    type: typeProp,
    payload: payloadProp,
    meta: metaProp,
    repeat: repeatProp,
    delay: delayProp,
    onClose,
    ...otherProps
  } = props;
  const [type, setType] = React.useState(typeProp || '');
  const [payload, setPayload] = React.useState<string>(payloadProp ? JSON.stringify(payloadProp, null, 2) : '');
  const [meta, setMeta] = React.useState(metaProp);
  const [repeat, setRepeat] = React.useState(repeatProp || 1);
  const [delay, setDelay] = React.useState(delayProp || 0);
  const [intervalId, setIntervalId] = React.useState<NodeJS.Timeout>();
  const [current, setCurrent] = React.useState<number>();
  const [payloadError, setPayloadError] = React.useState<string>();
  const [metaError, setMetaError] = React.useState<string>();

  const dispatchAction = React.useCallback(() => {
    let error = false;
    let payloadObj = {};
    try {
      setPayloadError(undefined);
      payloadObj = payload && JSON.parse(payload);
    } catch (e) {
      error = true;
      setPayloadError(String(e));
    }
    const payloadRecord =
      payloadProp || payloadObj ? (payloadProp || Record(payloadObj)()).merge(payloadObj) : undefined;

    let metaObj = {};
    try {
      setMetaError(undefined);
      metaObj = meta && JSON.parse(meta);
    } catch (e) {
      error = true;
      setMetaError(String(e));
    }
    const metaRecord = metaProp || metaObj ? (metaProp || Record(metaObj)()).merge(metaObj) : undefined;

    if (!error) {
      const actionCreator = actionHelpers.createAction(type);
      const action = actionCreator(payloadRecord, metaRecord);
      let n = 0;
      setCurrent(n);
      const interval = setInterval(() => {
        dispatch(action as AnyAction);
        n += 1;
        setCurrent(n);
        if (n >= repeat) {
          clearInterval(interval);
          setIntervalId(undefined);
          setCurrent(undefined);
        }
      }, delay);
      setIntervalId(interval);
    }
  }, [type, payload, payloadProp, meta, repeat, delay, setIntervalId, setCurrent, dispatch, metaProp]);

  return (
    <StdDialog
      // @ts-expect-error FIXME TS7006
      onClose={(event, reason) => {
        if (reason !== 'ENTER_KEY_DOWN') onClose(event, reason);
      }}
      size='md'
      title='Send Request'
      {...otherProps}
    >
      <DialogContent>
        <Stack>
          <TextField
            onChange={(event) => setType(event.target.value)}
            value={type}
            label='Type'
            variant='filled'
            style={{ width: 200 }}
            onKeyDown={(event) => {
              event.stopPropagation();
              if (event.key === 'Enter') {
                dispatchAction();
              }
            }}
          />
          <br />
          <TextField
            onChange={(event) => setPayload(event.target.value)}
            value={payload}
            fullWidth
            label='Payload'
            variant='filled'
            multiline
            onKeyDown={(event) => event.stopPropagation()}
            error={Boolean(payloadError)}
            helperText={payloadError}
          />
          <br />
          <TextField
            onChange={(event) => setMeta(event.target.value)}
            value={meta}
            fullWidth
            label='Meta'
            variant='filled'
            multiline
            onKeyDown={(event) => event.stopPropagation()}
            error={Boolean(metaError)}
            helperText={metaError}
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          id='abort'
          disabled={!intervalId}
          color='error'
          onClick={(event) => {
            event.stopPropagation();
            clearInterval(intervalId);
            setIntervalId(undefined);
            setCurrent(undefined);
          }}
        >
          Abort {typeof repeat === 'number' && typeof current === 'number' && repeat - current}
        </Button>
        <TextField
          disabled={Boolean(intervalId)}
          label='delay'
          value={delay}
          onChange={(event) => setDelay(Number(event.target.value))}
          type='number'
          InputProps={{
            inputProps: { min: 0 },
            endAdornment: <InputAdornment position='end'>ms</InputAdornment>,
          }}
        />
        <TextField
          disabled={Boolean(intervalId)}
          label='repeat'
          value={repeat}
          onChange={(event) => setRepeat(Number(event.target.value))}
          type='number'
          InputProps={{
            inputProps: { min: 1 },
            endAdornment: <InputAdornment position='end'>time(s)</InputAdornment>,
          }}
        />
        <Button
          id='dispatch-action'
          disabled={Boolean(intervalId)}
          onKeyDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation();
            dispatchAction();
          }}
        >
          Dispatch
        </Button>
      </DialogActions>
    </StdDialog>
  );
};

export default DispatchDialog;
